import React, { useCallback, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";

import useMutation from "src/hooks/useMutation";
import {
  DELETE_INTERNAL_WORK_ORDER_ITEM_MUTATION,
  UPDATE_INTERNAL_WORK_ORDER_ITEM_MUTATION,
} from "src/constants/graphql/mutations";
import ConfirmDeleteModal from "src/components/images/ConfirmDeleteModal";
import CartItemQuantity from "src/views/inventory/requests/CartItemQuantity";
import Dimensions from "src/views/inventory/requests/Dimensions";
import ItemId from "src/views/shopping/Orders/ItemId";
import { getImage } from "src/views/shopping/Cart/CartItemFields";
import CardImage from "src/components/card/CardImage";
import DetailRow from "src/components/card/DetailRow";
import labels from "src/constants/displayFields";

export default function InternalWorkOrderItemMobileCard({ item, display, onDelete }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInternalWorkOrderItem, { loading: deleteLoading }] = useMutation(
    DELETE_INTERNAL_WORK_ORDER_ITEM_MUTATION
  );
  const [updateInternalWorkOrderItem, { loading: updateLoading }] = useMutation(
    UPDATE_INTERNAL_WORK_ORDER_ITEM_MUTATION
  );

  const asset = item?.asset || {};
  const photo = useMemo(() => getImage(asset), [asset]);

  const handleDelete = useCallback(async () => {
    await deleteInternalWorkOrderItem({
      variables: { id: item.id },
      onSuccess: async () => {
        if (typeof onDelete === "function") onDelete();
        setShowDeleteModal(false);
      },
      onFailure: error => {
        console.error("Failed to delete internal work order item:", error);
      },
    });
  }, [deleteInternalWorkOrderItem, item?.id, onDelete]);

  const handleQuantityChange = useCallback(
    async newQuantity => {
      const input = { quantity: newQuantity };
      await updateInternalWorkOrderItem({
        variables: { id: item.id, input },
        onSuccess: async () => {
          if (typeof onDelete === "function") onDelete();
        },
        onFailure: () => {
          console.error("Failed to update internal work order item quantity");
        },
      });
    },
    [updateInternalWorkOrderItem, item?.id, onDelete]
  );

  return (
    <Card sx={{ mb: 1.5 }}>
      {(updateLoading || deleteLoading) && (
        <Box mt={-0.5}>
          <LinearProgress sx={{ height: "2px" }} />
        </Box>
      )}
      <CardContent sx={{ "&:last-child": { pb: 2 }, position: "relative" }}>
        {display.ii_image && <CardImage image={photo} />}
        <Box display="flex" flexDirection="column" gap={1}>
          {display.ii_item_id && (
            <DetailRow label="Item ID">
              <Typography variant="body2" fontWeight={500}>
                <ItemId asset={asset} />
              </Typography>
            </DetailRow>
          )}
          {display.ii_sku_number && asset.skuNumber && (
            <DetailRow label={labels.sku_number}>
              <Typography variant="body2" noWrap>
                {asset.skuNumber}
              </Typography>
            </DetailRow>
          )}
          {display.ii_barcode && asset.barcode && (
            <DetailRow label="Barcode" value={asset.barcode} />
          )}
          {display.ii_model && asset.model && <DetailRow label="Model" value={asset.model} />}
          {display.ii_dimensions && Array.isArray(asset.dimensions) && asset.dimensions.length > 0 && (
            <DetailRow label="Dimensions (W x D x H)">
              <Dimensions dimensions={asset.dimensions} />
            </DetailRow>
          )}
          {asset?.description && (
            <DetailRow label="Description">
              <Typography variant="body2" noWrap>
                {asset.description}
              </Typography>
            </DetailRow>
          )}

          {display.ii_qtyRequested && (
            <>
              <Box my={0.5}>
                <Divider />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                {item.nonAllocating ? (
                  <Chip label="Non-allocating" size="small" variant="outlined" />
                ) : (
                  <CartItemQuantity
                    disabled={updateLoading || deleteLoading}
                    value={item.quantity || ""}
                    availableQuantity={asset?.availableQuantity}
                    onChange={quantity => handleQuantityChange(quantity)}
                  />
                )}
                <IconButton disabled={deleteLoading} onClick={() => setShowDeleteModal(true)}>
                  {deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
                </IconButton>
              </Box>
            </>
          )}
        </Box>

        {showDeleteModal && (
          <ConfirmDeleteModal
            title="Remove Item"
            applyLabel="Remove"
            descr="Are you sure you want to remove the selected item from this internal work order?"
            onClose={() => setShowDeleteModal(false)}
            onDelete={handleDelete}
          />
        )}
      </CardContent>
    </Card>
  );
}
